import { filter, orderBy } from "lodash-es";

import {
  type AssistantFollowUpObservable,
  type AssistantProjectObservable,
  type AssistantTaskObservable,
} from "@/store/assistant";
import { getAgentTimestamp } from "@/pages/agent/utils/agent-date.utils";
import { isClosedFollowUp } from "@/pages/agent/utils/agent-follow-up.utils";
import type { TaskSourceNote } from "@/pages/agent/utils/agent-task-source.utils";

export type TaskDetailViewModel = {
  task: AssistantTaskObservable;
  project: AssistantProjectObservable | null;
  projectTitle: string;
  projectLoading: boolean;
  nextFollowUp: AssistantFollowUpObservable | null;
  followUpCount: number;
  followUpsLoading: boolean;
  sourceNotes: TaskSourceNote[];
  sourcesLoading: boolean;
};

export const buildTaskDetailViewModel = ({
  task,
  projects,
  projectsLoaded,
  followUps,
  followUpsLoaded,
  sourceNotes,
  sourcesLoaded,
}: {
  task: AssistantTaskObservable;
  projects: AssistantProjectObservable[];
  projectsLoaded: boolean;
  followUps: AssistantFollowUpObservable[];
  followUpsLoaded: boolean;
  sourceNotes: TaskSourceNote[];
  sourcesLoaded: boolean;
}): TaskDetailViewModel => {
  const project = task.projectId
    ? (projects.find(candidate => candidate.id === task.projectId) ?? null)
    : null;
  const taskFollowUps = filter(followUps, followUp => followUp.taskId === task.id);
  const openFollowUps = filter(taskFollowUps, followUp => !isClosedFollowUp({ followUp }));
  const nextFollowUp =
    orderBy(
      openFollowUps,
      followUp =>
        getAgentTimestamp({
          value: followUp.scheduledFor ?? followUp.dueAt,
          fallback: Number.POSITIVE_INFINITY,
        }),
      "asc"
    )[0] ?? null;
  const projectLoading = Boolean(task.projectId) && !project && !projectsLoaded;

  return {
    task,
    project,
    projectTitle: project?.title ?? (projectLoading ? "Loading project…" : "No project"),
    projectLoading,
    nextFollowUp,
    followUpCount: taskFollowUps.length,
    followUpsLoading: !followUpsLoaded,
    sourceNotes,
    sourcesLoading: !sourcesLoaded,
  };
};
